// src/contexts/CartContext.jsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { ecommerceService } from '../services/ecommerceService';

const CartContext = createContext();

const CART_ACTIONS = {
  LOAD_CART: 'LOAD_CART',
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  SET_LOADING: 'SET_LOADING'
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload,
        loading: false
      };

    case CART_ACTIONS.ADD_ITEM:
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id && 
        JSON.stringify(item.variant) === JSON.stringify(action.payload.variant)
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += action.payload.quantity;
        return {
          ...state,
          items: updatedItems
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => 
          !(item.id === action.payload.id && 
          JSON.stringify(item.variant) === JSON.stringify(action.payload.variant))
        )
      };

    case CART_ACTIONS.UPDATE_QUANTITY:
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id && 
          JSON.stringify(item.variant) === JSON.stringify(action.payload.variant)
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: []
      };

    case CART_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    loading: true
  });

  // Load cart from localStorage or Firestore
  useEffect(() => {
    loadCart();
  }, [user]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (!state.loading) {
      localStorage.setItem('cart', JSON.stringify(state.items));
      
      // Also save to Firestore if user is logged in
      if (user) {
        saveCartToFirestore();
      }
    }
  }, [state.items, user]);

  const loadCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      
      if (user) {
        // Load from Firestore for logged-in users
        try {
          const firestoreCart = await ecommerceService.getCart(user.uid);
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: firestoreCart || [] });
        } catch (error) {
          console.log('No Firestore cart found, loading from localStorage');
          loadFromLocalStorage();
        }
      } else {
        // Load from localStorage for guests
        loadFromLocalStorage();
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      const cartItems = savedCart ? JSON.parse(savedCart) : [];
      dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartItems });
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      dispatch({ type: CART_ACTIONS.LOAD_CART, payload: [] });
    }
  };

  const saveCartToFirestore = async () => {
    if (!user) return;
    
    try {
      await ecommerceService.saveCart(user.uid, state.items);
    } catch (error) {
      console.error('Error saving cart to Firestore:', error);
    }
  };

  const addToCart = async (product, quantity = 1, variant = null) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.salePrice || product.price,
      originalPrice: product.price,
      imageUrl: product.images?.[0] || product.imageUrl,
      sku: product.sku,
      brand: product.brand,
      category: product.category,
      storeId: product.storeId,
      quantity,
      variant,
      addedAt: new Date().toISOString()
    };

    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: cartItem });
    
    // Track analytics
    try {
      await ecommerceService.trackAddToCart(product.storeId, product.id, quantity);
    } catch (error) {
      console.error('Error tracking add to cart:', error);
    }
  };

  const removeFromCart = (productId, variant = null) => {
    dispatch({ 
      type: CART_ACTIONS.REMOVE_ITEM, 
      payload: { id: productId, variant } 
    });
  };

  const updateQuantity = (productId, quantity, variant = null) => {
    dispatch({ 
      type: CART_ACTIONS.UPDATE_QUANTITY, 
      payload: { id: productId, quantity, variant } 
    });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const getCartSubtotal = () => {
    return getCartTotal();
  };

  const getCartTax = (taxRate = 0.15) => {
    return getCartSubtotal() * taxRate;
  };

  const getCartShipping = (shippingMethod = null) => {
    if (!shippingMethod) return 0;
    return shippingMethod.price || 0;
  };

  const getCartGrandTotal = (taxRate = 0.15, shippingMethod = null) => {
    const subtotal = getCartSubtotal();
    const tax = getCartTax(taxRate);
    const shipping = getCartShipping(shippingMethod);
    return subtotal + tax + shipping;
  };

  const isInCart = (productId, variant = null) => {
    return state.items.some(item => 
      item.id === productId && 
      JSON.stringify(item.variant) === JSON.stringify(variant)
    );
  };

  const getCartItem = (productId, variant = null) => {
    return state.items.find(item => 
      item.id === productId && 
      JSON.stringify(item.variant) === JSON.stringify(variant)
    );
  };

  const calculateShippingForZone = (cart, deliveryZone, freeShippingThreshold) => {
    if (!deliveryZone) return 0;
    
    const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    
    // Check for free shipping
    if (freeShippingThreshold && subtotal >= freeShippingThreshold) {
      return 0;
    }
    
    return deliveryZone.fee || 0;
  };

  const calculateTaxAmount = (cart, taxRate, taxInclusive = false) => {
    const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    
    if (taxInclusive) {
      // Tax is already included in prices
      return subtotal * (taxRate / (1 + taxRate));
    } else {
      // Add tax on top
      return subtotal * taxRate;
    }
  };

  const value = {
    cart: state.items,
    loading: state.loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    getCartSubtotal,
    getCartTax,
    getCartShipping,
    getCartGrandTotal,
    isInCart,
    getCartItem,
    calculateShippingForZone,
    calculateTaxAmount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};